import { useState, useEffect, useRef } from 'react';
import { ChevronDown, Leaf, Droplets, Building, Users, Wind } from 'lucide-react';

export default function EnvironmentComponent() {
  const [activeSection, setActiveSection] = useState(0);
  const sectionRefs = useRef([]);

  // Initialize refs array
  useEffect(() => {
    sectionRefs.current = sectionRefs.current.slice(0, sections.length);
  }, []);

  // Scroll to next section
  const scrollToNext = () => {
    if (activeSection < sections.length - 1) {
      const nextIndex = activeSection + 1;
      setActiveSection(nextIndex);
      sectionRefs.current[nextIndex]?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Handle scroll events to determine active section
  useEffect(() => {
    const handleScroll = () => {
      const pageTop = window.pageYOffset;
      const pageBottom = pageTop + window.innerHeight;
      
      sectionRefs.current.forEach((ref, index) => {
        if (!ref) return;
        
        const el = ref;
        const rect = el.getBoundingClientRect();
        const elTop = rect.top + pageTop;
        const elBottom = elTop + rect.height;
        
        // If element is in viewport
        if (elTop < pageBottom && elBottom > pageTop) {
          setActiveSection(index);
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const sections = [
    {
      title: "Dutch-India Environmental Collaboration",
      content: "A groundbreaking partnership combining Dutch environmental leadership with India's ecological diversity to create sustainable solutions for pressing environmental challenges. Together, we're building a greener, more resilient future for generations to come.",
      video: "/videos/environmental collaboration.mp4",
videoAlt: "Some description"

    },
    {
      title: "Strengthen Ecosystem Conservation Initiatives",
      content: "Collaborate with Dutch environmental agencies to protect India's unique ecosystems such as the Coringa mangroves, Pulicat Lake, and Eastern Ghats through joint research, biodiversity monitoring, and habitat restoration programs. These efforts preserve the region's natural heritage while creating models for effective conservation worldwide.",
      video: "/videos/ecosystem conservation.mp4",
      videoAlt: "Some description"
      
    },
    {
      title: "Promote Clean and Green Technologies",
      content: "Leverage Dutch expertise in water management, waste treatment, and renewable energy to implement eco-friendly technologies across urban and rural areas in India, focusing on reducing pollution and enhancing sustainability. This technology transfer creates environmentally responsible solutions tailored to local conditions.",
      video: "/videos/green technologies.mp4",
      videoAlt: "Some description"
      
    },
    {
      title: "Develop Green Urban Infrastructure",
      content: "Co-design sustainable urban projects including green buildings, smart parks, eco-mobility systems, and climate-resilient infrastructure in partnership with Dutch architects and urban planners. These innovative developments transform cities into healthy, sustainable environments that enhance quality of life.",
      video: "/videos/urban infrastructure.mp4",
      videoAlt: "Some description"
      
    },
    {
      title: "Launch Indo-Dutch Environmental Awareness Campaigns",
      content: "Conduct awareness drives, school programs, and community projects to promote environmental responsibility, recycling, and climate action, drawing on Dutch best practices and local engagement. These initiatives empower citizens to become environmental stewards in their communities.",
      video: "/videos/awareness campaigns.mp4",
videoAlt: "Some description"

    },
    {
      title: "Expected Outcomes",
      outcomes: [
        {
          icon: <Wind className="w-12 h-12 text-green-600 mb-4" />,
          title: "Climate Resilience",
          content: "Enhance climate resilience and environmental sustainability across the state."
        },
        {
          icon: <Leaf className="w-12 h-12 text-green-600 mb-4" />,
          title: "Biodiversity Protection",
          content: "Restore and protect critical natural habitats and biodiversity."
        },
        {
          icon: <Droplets className="w-12 h-12 text-green-600 mb-4" />,
          title: "Reduced Carbon Footprint",
          content: "Reduce carbon footprint through adoption of clean technologies."
        },
        {
          icon: <Users className="w-12 h-12 text-green-600 mb-4" />,
          title: "Public Awareness",
          content: "Raise public awareness and participation in environmental conservation."
        },
        {
          icon: <Building className="w-12 h-12 text-green-600 mb-4" />,
          title: "Green Economy",
          content: "Create green jobs and promote eco-conscious economic development."
        }
      ]
    }
  ];

  return (
    <div className="bg-white">
      {sections.map((section, index) => (
        <div
          key={index}
          ref={el => sectionRefs.current[index] = el}
          className={`min-h-screen flex flex-col justify-center items-center p-8 ${
            index % 2 === 0 ? 'bg-green-50' : 'bg-white'
          }`}
        >
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-green-800 mb-6">{section.title}</h2>
            
            {/* Regular section with content and image */}
            {!section.outcomes && (
              <>
                <p className="text-xl text-gray-700 mb-12">{section.content}</p>
                {section.video && (
      <div className="rounded-xl overflow-hidden shadow-xl mb-10 border-2 border-green-100">
        <video
          src={section.video}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-auto object-cover rounded-xl"
        />
      </div>
    )}
  </>
)}


            
            {/* Outcomes section */}
            {section.outcomes && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center mt-8">
                {section.outcomes.map((outcome, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-lg shadow-lg flex flex-col items-center border-t-4 border-green-500">
                    {outcome.icon}
                    <h3 className="text-xl font-bold text-green-700 mb-3">{outcome.title}</h3>
                    <p className="text-gray-700">{outcome.content}</p>
                  </div>
                ))}
              </div>
            )}
            
            {/* Show scroll down indicator if not the last section */}
            {index < sections.length - 1 && (
              <button 
                onClick={scrollToNext}
                className="mt-10 text-green-600 hover:text-green-800 flex flex-col items-center animate-bounce"
              >
                <span className="mb-2">Discover more</span>
                <ChevronDown className="w-6 h-6" />
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}